import {Button, Dropdown, Modal} from "antd";
import type {MenuProps} from "antd";
import {DeleteOutlined, EditOutlined, MoreOutlined} from "@ant-design/icons";
import React from "react";
import User from "../../types/User";
import {roleLabel} from "./UserRole";
import {initials} from "../apps/TopBar";
import styles from "./Team.module.css";

interface UserContainerProps {
    user: User
    isSelf: boolean
    onEdit: (user: User) => void
    onDelete: (user: User) => void
}

const isAdminRole = (role: string) => /ADMIN|MANAGE/.test(role);

export default function UserContainer({user, isSelf, onEdit, onDelete}: UserContainerProps) {
    const adminRoles = user.roles.filter(isAdminRole);

    const confirmRemove = () => {
        Modal.confirm({
            title: `Remove ${user.username}?`,
            content: "They won't be able to sign in any more.",
            okText: "Remove",
            okButtonProps: {danger: true},
            cancelText: "Cancel",
            onOk: () => onDelete(user),
        });
    };

    const menu: MenuProps["items"] = [
        {key: "edit", icon: <EditOutlined/>, label: "Edit member", onClick: () => onEdit(user)},
        {type: "divider"},
        {
            key: "delete",
            icon: <DeleteOutlined/>,
            label: isSelf ? "You can't remove yourself" : "Remove from team",
            danger: !isSelf,
            disabled: isSelf,
            onClick: confirmRemove,
        },
    ];

    return (
        <li className={styles.member}>
            <span className={styles.initial} aria-hidden>{initials(user.username)}</span>
            <div className={styles.memberText}>
                <span className={styles.username}>{user.username}</span>
                {isSelf && <span className={styles.you}>You</span>}
                {adminRoles.map(role => (
                    <span key={role} className={`${styles.role} ${styles.roleAdmin}`} title={role}>{roleLabel(role)}</span>
                ))}
            </div>
            <Dropdown menu={{items: menu}} trigger={["click"]} placement="bottomRight">
                <Button type="text" size="small" icon={<MoreOutlined/>} aria-label={`Actions for ${user.username}`}/>
            </Dropdown>
        </li>
    );
}
